/* Famli: viewport-driven muted video autoplay across the unpublished theme.
   Native videos, video-element sections, product media and story carousels.
   Do not autoplay hidden slides. Pause when scrolled away or tab is hidden.
   Reduced-motion preference remains respected. */
(() => {
  'use strict';
  if (window.__famliScrollAutoplay || !('IntersectionObserver' in window)) return;
  window.__famliScrollAutoplay = true;

  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mediaStates = new Map();
  let active = null;
  let selectionPending = false;
  let scanPending = false;

  const pause = (element) => {
    if (!element) return;
    try {
      if (element.matches('video-element')) {
        element.pause();
      } else if (element.tagName === 'VIDEO' && !element.paused) {
        element.pause();
      }
    } catch (_) {
      /* Video may not have loaded yet. */
    }
  };

  const prepareVideo = (video) => {
    if (!video || video.tagName !== 'VIDEO') return;
    video.defaultMuted = true;
    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.loop = true;
    if (video.getAttribute('preload') === 'none') video.preload = 'metadata';
  };

  const start = (element) => {
    if (!element || !element.isConnected) return;
    const state = mediaStates.get(element);
    if (!state || state.failed) return;

    if (element.matches('video-element')) {
      if (element.playing || element.paused === true) return;
      const template = element.querySelector('template');
      if (template) prepareVideo(template.content.querySelector('video'));
      const directVideo = element.querySelector('video');
      if (directVideo) prepareVideo(directVideo);
      // The theme's YouTube and Vimeo players use this flag to mute on init.
      if (!element.hasAttribute('autoplay')) element.setAttribute('autoplay', '');
      try {
        element.play();
      } catch (_) {
        state.failed = true;
      }
      return;
    }

    if (element.tagName !== 'VIDEO' || !element.paused) return;
    prepareVideo(element);
    if (element.closest('[data-nap-video]')) element.controls = true;

    try {
      const result = element.play();
      if (result && typeof result.catch === 'function') {
        result.catch(() => {
          state.failed = true;
          if (active === element) active = null;
        });
      }
    } catch (_) {
      state.failed = true;
      if (active === element) active = null;
    }
  };

  const eligible = (element, state) => {
    if (!element.isConnected || document.hidden || motionPreference.matches || state.failed) return false;
    if (element.closest('[hidden],[inert],[aria-hidden="true"]')) return false;

    const carouselSlide = element.closest('.famli-carousel__slide');
    if (carouselSlide && carouselSlide.inert) return false;

    const swiperSlide = element.closest('.swiper-slide');
    if (swiperSlide && swiperSlide.parentElement &&
        swiperSlide.parentElement.querySelector('.swiper-slide-active') &&
        !swiperSlide.classList.contains('swiper-slide-active')) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;

    const rect = element.getBoundingClientRect();
    if (rect.width < 16 || rect.height < 16) return false;
    const requiredHeight = Math.min(145, rect.height * 0.32);
    const requiredWidth = Math.min(75, rect.width * 0.32);
    return state.ratio >= 0.24 &&
      state.visibleHeight >= requiredHeight &&
      state.visibleWidth >= requiredWidth;
  };

  const choose = () => {
    if (document.hidden || motionPreference.matches) {
      if (active) pause(active);
      active = null;
      return;
    }

    let best = null;
    let bestScore = -Infinity;
    for (const [element, state] of mediaStates) {
      if (!eligible(element, state)) continue;
      const rect = element.getBoundingClientRect();
      const centerDistance = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
      const score = state.ratio * 1000 + state.visibleHeight * 0.18 - centerDistance * 0.08;
      if (score > bestScore) {
        best = element;
        bestScore = score;
      }
    }
    // Do not repeatedly play() an already selected video: manual pause wins.
    if (active === best) return;
    if (active) pause(active);
    active = best;
    if (best) start(best);
  };

  const scheduleChoose = () => {
    if (selectionPending) return;
    selectionPending = true;
    requestAnimationFrame(() => {
      selectionPending = false;
      choose();
    });
  };

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const state = mediaStates.get(entry.target);
      if (!state) continue;
      state.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
      state.visibleHeight = entry.isIntersecting ? entry.intersectionRect.height : 0;
      state.visibleWidth = entry.isIntersecting ? entry.intersectionRect.width : 0;
      if (!entry.isIntersecting) state.failed = false;
    }
    scheduleChoose();
  }, { threshold: [0, 0.15, 0.25, 0.4, 0.6, 0.8, 1] });

  const register = (element) => {
    if (mediaStates.has(element)) return;
    if (element.tagName === 'VIDEO' && element.closest('video-element')) return;
    if (element.closest('[data-famli-no-autoplay]')) return;
    mediaStates.set(element, { ratio: 0, visibleHeight: 0, visibleWidth: 0, failed: false });
    observer.observe(element);

    if (element.matches('video-element')) {
      // Asynchronous autoplay must stop if the customer has scrolled away.
      // Do not cancel a visible video that the customer started manually.
      element.addEventListener('video:play', () => {
        const current = mediaStates.get(element);
        if (current && !eligible(element, current)) pause(element);
      });
    }
  };

  const scan = () => {
    for (const element of mediaStates.keys()) {
      if (!element.isConnected) {
        observer.unobserve(element);
        mediaStates.delete(element);
        if (active === element) active = null;
      }
    }
    document.querySelectorAll('video-element,video').forEach(register);
    scheduleChoose();
  };

  const scheduleScan = () => {
    if (scanPending) return;
    scanPending = true;
    requestAnimationFrame(() => {
      scanPending = false;
      scan();
    });
  };

  document.addEventListener('play', (event) => {
    if (event.target.tagName !== 'VIDEO') return;
    const frame = event.target.closest('[data-nap-video]');
    if (frame) frame.classList.add('is-playing');
  }, true);

  document.addEventListener('pause', (event) => {
    if (event.target.tagName !== 'VIDEO') return;
    const frame = event.target.closest('[data-nap-video]');
    if (frame) frame.classList.remove('is-playing');
  }, true);

  document.addEventListener('ended', (event) => {
    if (event.target.tagName !== 'VIDEO') return;
    const frame = event.target.closest('[data-nap-video]');
    if (frame) frame.classList.remove('is-playing');
  }, true);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (active) pause(active);
      active = null;
    } else {
      scheduleChoose();
    }
  });

  motionPreference.addEventListener?.('change', scheduleChoose);
  document.addEventListener('shopify:section:load', scheduleScan);
  document.addEventListener('shopify:section:unload', scheduleScan);
  new MutationObserver((records) => {
    if (records.some((record) => record.addedNodes.length || record.removedNodes.length)) scheduleScan();
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
})();